// Adapted from Hedge-Fund-Tracking-System apps/web/src/components/layout/top-bar.tsx.
// Same height/spacing/typography. Replaces the fund-selector with the bot's
// live status (mode pill + last-tick indicator); keeps ThemeToggle.
import { cn, formatDuration, shortenAddress } from '../../lib/utils';
import { ThemeToggle } from './theme-toggle';
import { Bot, Check, ChevronDown } from 'lucide-react';
import { useState as useReactState, useRef, useEffect } from 'react';
import { useState } from '../../hooks/useApi';
import { useSelectedStrategy } from '../../hooks/useSelectedStrategy';
import { useAuth } from '../../lib/auth-context';

interface TopBarProps {
  className?: string;
}

const ROLE_LABELS: Record<string, string> = {
  admin: 'Admin',
  fund_manager: 'Manager',
  investor: 'Investor',
};

export function TopBar({ className }: TopBarProps) {
  const { data: state } = useState();
  const { user } = useAuth();
  const { selectedStrategyId, setSelectedStrategyId } = useSelectedStrategy();
  const [showMenu, setShowMenu] = useReactState(false);
  const [showUserMenu, setShowUserMenu] = useReactState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const available = state?.availableStrategies ?? [];
  const currentStrategyId = state?.strategy?.id;
  // "Aggregate mode" = no specific strategy selected → server returned
  // data scoped to all active strategies. Label the dropdown explicitly
  // so the operator never confuses an aggregate view for a specific one.
  const isAggregateMode = selectedStrategyId === undefined;
  const currentStrategyName = isAggregateMode
    ? `All active strategies${available.length > 0 ? ` (${available.length})` : ''}`
    : state?.strategy?.displayName ?? 'Aerodrome × Hyperliquid';

  // Auto-correct stale localStorage: if the selected strategy is NOT in
  // the active list (e.g. user closed it from another tab, or pulled a
  // fresh DB), drop the localStorage pointer so subsequent reloads use
  // the server's default. Without this, the user gets pinned to a
  // closed strategy with no UI escape.
  useEffect(() => {
    if (
      selectedStrategyId !== undefined &&
      available.length > 0 &&
      !available.some((s) => s.id === selectedStrategyId)
    ) {
      setSelectedStrategyId(undefined);
    }
  }, [selectedStrategyId, available, setSelectedStrategyId]);

  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setShowMenu(false);
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node))
        setShowUserMenu(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const features = state?.mode.features ?? [];
  const modeLabel =
    features.length === 0 ? 'Observer' : `auto-${features.join('+')}`;
  const modePillClass =
    features.length === 0
      ? 'bg-muted text-muted-foreground border-border'
      : state?.mode.dryRun
      ? 'bg-warning-muted text-warning border-warning/20'
      : 'bg-positive-muted text-positive border-positive/20';

  const tickAge = state?.lastTickAgeMs;
  let dotClass = 'bg-muted-foreground';
  let dotText = '…';
  if (tickAge !== null && tickAge !== undefined) {
    if (tickAge < 60_000) {
      dotClass = 'bg-positive';
      dotText = `tick ${Math.floor(tickAge / 1000)}s ago`;
    } else if (tickAge < 5 * 60_000) {
      dotClass = 'bg-warning';
      dotText = `last tick ${formatDuration(tickAge)} ago`;
    } else {
      dotClass = 'bg-negative';
      dotText = `stale (${formatDuration(tickAge)})`;
    }
  }

  return (
    <header
      className={cn(
        'h-16 flex items-center justify-between px-6 bg-surface border-b border-border shrink-0',
        className,
      )}
    >
      {/* Bot identity / context */}
      <div className="relative" ref={menuRef}>
        <button
          onClick={() => setShowMenu(!showMenu)}
          className="flex items-center gap-2 rounded-lg px-3 py-2 hover:bg-accent transition-colors"
        >
          <div className="h-7 w-7 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
            <Bot className="h-3.5 w-3.5 text-primary" />
          </div>
          <div className="text-left hidden sm:block">
            <div className="flex items-center gap-2">
              <p className="text-sm font-semibold text-foreground leading-none">
                {currentStrategyName}
              </p>
              <span
                className={cn(
                  'px-1.5 py-0.5 text-[10px] rounded-full border font-medium tracking-tight',
                  modePillClass,
                )}
              >
                {modeLabel}
                {state?.mode.dryRun && ' • DRY'}
              </span>
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              {state?.mode.hlNetwork ?? '…'} · NFT #{state?.position.activeNftId ?? '—'} ·{' '}
              <span className="font-mono">{shortenAddress(state?.position.poolAddress)}</span>
            </p>
          </div>
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        </button>

        {showMenu && state && (
          <div className="absolute left-0 top-full mt-1 w-80 rounded-lg border border-border bg-surface shadow-lg py-2 z-50">
            {/* Strategy switcher — rendered whenever there's at least one
                active strategy. Previously hidden when only one existed,
                which left users pinned to a stale localStorage selection
                with no UI escape if their previously-selected strategy
                got closed. */}
            {available.length >= 1 && (
              <div className="px-3 py-2 border-b border-border">
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                  Switch strategy
                </p>
                <div className="space-y-0.5">
                  {/* "All active strategies" option — clears the URL/localStorage
                      selection so subsequent API calls go without a strategyId
                      param and the server returns aggregate-active data. */}
                  <button
                    onClick={() => {
                      setSelectedStrategyId(undefined);
                      setShowMenu(false);
                    }}
                    className={cn(
                      'w-full flex items-center justify-between rounded-md px-2 py-1.5 text-sm transition-colors',
                      isAggregateMode
                        ? 'bg-primary/10 text-primary'
                        : 'text-foreground hover:bg-accent',
                    )}
                  >
                    <span className="flex items-center gap-2">
                      <span className="font-medium">All active strategies</span>
                      <span className="text-xs text-muted-foreground tabular-nums">
                        cumulative · {available.length}
                      </span>
                    </span>
                    {isAggregateMode && <Check className="h-3.5 w-3.5" />}
                  </button>
                  {available.map((s) => {
                    const isActive =
                      !isAggregateMode &&
                      (selectedStrategyId ?? currentStrategyId) === s.id;
                    return (
                      <button
                        key={s.id}
                        onClick={() => {
                          setSelectedStrategyId(s.id);
                          setShowMenu(false);
                        }}
                        className={cn(
                          'w-full flex items-center justify-between rounded-md px-2 py-1.5 text-sm transition-colors',
                          isActive
                            ? 'bg-primary/10 text-primary'
                            : 'text-foreground hover:bg-accent',
                        )}
                      >
                        <span className="flex items-center gap-2">
                          <span className="font-medium">{s.displayName}</span>
                          <span className="text-xs text-muted-foreground tabular-nums">
                            {s.slug}
                          </span>
                          {s.mode === 'paper' && (
                            <span className="px-1.5 py-0.5 text-[10px] rounded-full border border-warning/20 bg-warning-muted text-warning">
                              paper
                            </span>
                          )}
                          {/* Hedge-model badge so the operator can see at
                              a glance which strategies are A/B test arms.
                              Only show 'static' explicitly; 'continuous' is
                              the legacy default and would just be noise. */}
                          {s.hedgeModel === 'static' && (
                            <span
                              className="px-1.5 py-0.5 text-[10px] rounded-full border border-primary/30 bg-primary/10 text-primary"
                              title="Hedge opens once at epoch start, frozen until next redeploy (no in-epoch rebalances)."
                            >
                              static
                            </span>
                          )}
                        </span>
                        {isActive && <Check className="h-3.5 w-3.5" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="px-3 py-2">
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                Position
              </p>
              <div className="space-y-1.5 text-xs">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Active NFT</span>
                  <span className="tabular-nums">{state.position.activeNftId ?? '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Pool</span>
                  <span className="font-mono">{shortenAddress(state.position.poolAddress)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">HL coin</span>
                  <span>{state.position.hlCoin}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Right actions */}
      <div className="flex items-center gap-3 shrink-0">
        <div className="hidden sm:flex items-center gap-2 text-xs text-muted-foreground">
          <span className={cn('h-2 w-2 rounded-full', dotClass)} />
          <span>{dotText}</span>
        </div>
        <ThemeToggle />

        {user && (
          <div className="relative pl-2 border-l border-border ml-1" ref={userMenuRef}>
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-2 rounded-md px-1 py-1 hover:bg-accent transition-colors"
            >
              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <span className="text-primary text-xs font-semibold">
                  {user.name
                    .split(' ')
                    .map((n) => n[0])
                    .join('')
                    .slice(0, 2)
                    .toUpperCase() || user.id.slice(0, 2).toUpperCase()}
                </span>
              </div>
              <div className="hidden sm:block text-left">
                <p className="text-sm font-medium text-foreground leading-none">
                  {user.name || user.id}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {ROLE_LABELS[user.role] ?? user.role}
                </p>
              </div>
            </button>

            {showUserMenu && (
              <div className="absolute right-0 top-full mt-1 w-56 rounded-lg border border-border bg-surface shadow-lg py-2 z-50">
                <div className="px-3 py-2">
                  <p className="text-sm font-medium text-foreground">{user.name || user.id}</p>
                  <p className="text-xs text-muted-foreground">{user.email}</p>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    {ROLE_LABELS[user.role] ?? user.role}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
